/**
 * The filter query-string codec — `?v=1&t=balanced&q=60&ch=reed-richards`.
 *
 * Pure string in, pure state out: no `window`, no catalog. Everything that
 * makes a permalink durable lives here.
 *
 * Four rules, in priority order:
 *
 * 1. **Decoding never throws and never rejects.** A share link from six
 *    months ago has to render *something* sensible. Unknown params are
 *    ignored, missing params fall back to their default, malformed values are
 *    dropped, out-of-range numbers are clamped.
 * 2. **Defaults are omitted.** The complete road (the default state) has an
 *    empty query string, and every param present in a URL is a decision
 *    somebody actually made.
 * 3. **The track is a base layer, not a duplicate.** `t=bare` carries a whole
 *    preset in six bytes, and anything you changed on top of it rides along as
 *    an override — `?v=1&t=bare&q=70`. Encoding tries each preset as the base
 *    and keeps whichever spells the state in the fewest params, so a URL is as
 *    short as it can be without ever being ambiguous.
 *
 *    **The obligation this creates:** `t` resolves against the *current*
 *    preset table, so re-tuning what "Bare Minimum" means would silently
 *    change every link that used it as a base. That is what `v` is for — if a
 *    preset's definition ever changes, bump {@link FILTERS_CODEC_VERSION},
 *    keep the old table, and resolve `t` against the table matching the link's
 *    version. Don't re-tune a preset in place.
 * 4. **Round-trip exact.** `decodeFilters(encodeFilters(s)) === s` for every
 *    normalized `s` — asserted as a property test in tests/filters.test.ts.
 *
 * `v` is the codec version. It is emitted only alongside real params (so the
 * empty case stays empty) and a missing `v` is read as the current version:
 * v0.1 links predate nothing, and a future codec bump reads old links by
 * branching here rather than by rejecting them.
 */

import { isTrackPreset, presetFilters, TRACK_PRESETS, type TrackPreset } from "./presets.ts";
import {
  DEFAULT_FILTERS,
  type FilterState,
  normalizeFilters,
  QUALITY_MAX,
  QUALITY_MIN,
  RUNTIME_CAP_MAX,
  RUNTIME_CAP_MIN,
  TIER_RANK_MAX,
  TIER_RANK_MIN,
} from "./types.ts";

/** Bump only on a breaking change to the *meaning* of these params. */
export const FILTERS_CODEC_VERSION = 1;

/** Short, stable param names. Never rename one — rename it and old links break. */
const KEY = {
  version: "v",
  track: "t",
  tier: "tr",
  quality: "q",
  qualityEssentials: "qe",
  characters: "ch",
  threads: "th",
  runtime: "rt",
  series: "sr",
  alternatives: "alt",
} as const;

/** Catalog ids are lowercase slugs; anything else in a list is junk we drop. */
const ID_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
/** A ceiling on list length so a hand-mangled URL can't blow up the UI. */
const MAX_IDS = 64;

const intOrNull = (raw: string | null, min: number, max: number): number | null => {
  if (raw === null || raw.trim() === "") return null;
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  return Math.min(max, Math.max(min, Math.round(value)));
};

const boolOrNull = (raw: string | null): boolean | null => {
  if (raw === "1" || raw === "true") return true;
  if (raw === "0" || raw === "false") return false;
  return null;
};

const idsOrNull = (raw: string | null): string[] | null => {
  if (raw === null) return null;
  const ids = raw
    .split(",")
    .map((id) => id.trim().toLowerCase())
    .filter((id) => ID_PATTERN.test(id))
    .slice(0, MAX_IDS);
  // An explicitly empty list is a real value ("I cleared my selection"), and
  // decodes to []; encode omits it when it matches the base anyway.
  return ids;
};

/**
 * Query-string values here are slugs, digits and commas, none of which need
 * escaping — but encode defensively and put the commas back, because
 * `ch=a%2Cb` in a shared URL is exactly the kind of ugly that makes people
 * not share it.
 */
const encodeValue = (value: string): string =>
  encodeURIComponent(value).replace(/%2C/g, ",").replace(/%2F/g, "/");

/** Strip a leading "?" and tolerate a full URL or a bare query string. */
const toSearch = (input: string): string => {
  const trimmed = input.trim();
  const queryAt = trimmed.indexOf("?");
  const query = queryAt >= 0 ? trimmed.slice(queryAt + 1) : trimmed;
  const hashAt = query.indexOf("#");
  return hashAt >= 0 ? query.slice(0, hashAt) : query;
};

/** The params needed to turn `base` into `filters` — the whole compression scheme. */
function overrides(filters: FilterState, base: FilterState): [string, string][] {
  const pairs: [string, string][] = [];
  if (filters.tierMax !== base.tierMax) pairs.push([KEY.tier, String(filters.tierMax)]);
  if (filters.qualityMin !== base.qualityMin) pairs.push([KEY.quality, String(filters.qualityMin)]);
  if (filters.essentialsIgnoreQuality !== base.essentialsIgnoreQuality) {
    pairs.push([KEY.qualityEssentials, filters.essentialsIgnoreQuality ? "1" : "0"]);
  }
  if (filters.characters.join(",") !== base.characters.join(",")) {
    pairs.push([KEY.characters, filters.characters.join(",")]);
  }
  if (filters.threads.join(",") !== base.threads.join(",")) {
    pairs.push([KEY.threads, filters.threads.join(",")]);
  }
  if (filters.maxMinutes !== base.maxMinutes) {
    // `null` (no cap) as an override over a base that caps: 0 reads as "off".
    pairs.push([KEY.runtime, filters.maxMinutes === null ? "0" : String(filters.maxMinutes)]);
  }
  if (filters.includeSeries !== base.includeSeries) {
    pairs.push([KEY.series, filters.includeSeries ? "1" : "0"]);
  }
  if (filters.useAlternatives !== base.useAlternatives) {
    pairs.push([KEY.alternatives, filters.useAlternatives ? "1" : "0"]);
  }
  return pairs;
}

/**
 * Query string (no leading "?") for this state — `""` when nothing deviates
 * from the defaults.
 */
export function encodeFilters(state: FilterState): string {
  const filters = normalizeFilters(state);

  // Candidates in preference order: no track at all, then each preset as a
  // base. First one wins ties, so the plainest spelling is the canonical one
  // and encoding stays a pure function of the state.
  let pairs = overrides(filters, DEFAULT_FILTERS);
  let track: TrackPreset | null = null;
  for (const preset of TRACK_PRESETS) {
    const candidate = overrides(filters, presetFilters(preset.id));
    if (candidate.length + 1 < pairs.length + (track === null ? 0 : 1)) {
      pairs = candidate;
      track = preset.id;
    }
  }

  if (track !== null) pairs.unshift([KEY.track, track]);
  if (pairs.length === 0) return "";
  pairs.unshift([KEY.version, String(FILTERS_CODEC_VERSION)]);
  return pairs.map(([key, value]) => `${key}=${encodeValue(value)}`).join("&");
}

/**
 * Decode a query string (with or without "?", full URLs accepted) into a
 * complete, normalized state. Total function: every input produces a state.
 */
export function decodeFilters(input: string): FilterState {
  const params = new URLSearchParams(toSearch(input));

  // Version is read but not gated on: there is exactly one codec today, and a
  // future one branches here instead of refusing to render an old link.
  const rawTrack = params.get(KEY.track)?.trim().toLowerCase() ?? "";
  const base = isTrackPreset(rawTrack) ? presetFilters(rawTrack) : DEFAULT_FILTERS;

  const tierMax = intOrNull(params.get(KEY.tier), TIER_RANK_MIN, TIER_RANK_MAX);
  const qualityMin = intOrNull(params.get(KEY.quality), QUALITY_MIN, QUALITY_MAX);
  const essentialsIgnoreQuality = boolOrNull(params.get(KEY.qualityEssentials));
  const characters = idsOrNull(params.get(KEY.characters));
  const threads = idsOrNull(params.get(KEY.threads));
  const includeSeries = boolOrNull(params.get(KEY.series));
  const useAlternatives = boolOrNull(params.get(KEY.alternatives));

  // rt=0 (or anything below the floor) means "no cap"; absent means "inherit".
  const rawRuntime = params.get(KEY.runtime);
  const runtime = intOrNull(rawRuntime, 0, RUNTIME_CAP_MAX);
  const maxMinutes =
    rawRuntime === null || runtime === null
      ? base.maxMinutes
      : runtime < RUNTIME_CAP_MIN
        ? null
        : runtime;

  return normalizeFilters({
    tierMax: tierMax ?? base.tierMax,
    qualityMin: qualityMin ?? base.qualityMin,
    essentialsIgnoreQuality: essentialsIgnoreQuality ?? base.essentialsIgnoreQuality,
    characters: characters ?? base.characters,
    threads: threads ?? base.threads,
    maxMinutes,
    includeSeries: includeSeries ?? base.includeSeries,
    useAlternatives: useAlternatives ?? base.useAlternatives,
  });
}

/** `"?v=1&t=bare"`, or `""` for the default state — ready to concatenate onto a path. */
export function filtersToQuery(state: FilterState): string {
  const search = encodeFilters(state);
  return search === "" ? "" : `?${search}`;
}
